import { useReducer, useEffect, useState } from 'react';
import { webPortalApi } from '#config/web-portal-api';
import { requestStatuses as statuses, getRequestOptions } from '#helper';

const defaultState = {
  status: statuses.idle,
  devices: [],
  error: null
};

const actionTypes = Object.freeze({
  fetchStarted: 'fetchStarted',
  fetchSucceeded: 'fetchSucceeded',
  fetchFailed: 'fetchFailed',
  clear: 'clear'
});

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.fetchStarted:
      return { ...state, status: statuses.loading, error: null };
    case actionTypes.fetchSucceeded:
      return { ...state, status: statuses.succeeded, devices: action.payload };
    case actionTypes.fetchFailed:
      return { ...state, status: statuses.failed, devices: [], error: action.error };
    case actionTypes.clear:
      return defaultState;
    default:
      return state;
  }
};

/**
 * @typedef {Object} DeviceInfoState
 * @property {string} status - request status.
 * @property {object} device - device info.
 * @property {object} error - error.
 */

/**
 * Fetches device data.
 * Handles loading states.
 * @param {string} deviceUid - device uid.
 * @returns {DeviceInfoState} device info state.
 */

const useDeviceInfoData = deviceUids => {
  const [state, dispatch] = useReducer(reducer, defaultState);
  const [abortController] = useState(new AbortController());

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: actionTypes.fetchStarted });
        const url = webPortalApi.buildApiUrl(`device/distinct/details?uids=${deviceUids.slice(0, 100).join(',')}&occupierDetails=1`);
        const res = await fetch(url, getRequestOptions());
        const contentType = res.headers.get('content-type');

        if (!contentType || !contentType.includes('application/json')) {
          throw new TypeError('Response is not json');
        }
        if (!res.ok) {
          throw await res.json();
        }

        const devices = await res.json();

        if (!devices.length) {
          throw new Error('Device info is not found');
        }
        dispatch({
          type: actionTypes.fetchSucceeded,
          payload: devices
        });
      } catch (e) {
        if (e.name === 'AbortError') {
          return;
        }
        dispatch({
          type: actionTypes.fetchFailed,
          error: e
        });
      }
    };

    fetchData();
    return () => {
      abortController.abort();
    };
  }, [deviceUids, abortController, dispatch]);

  return state;
};

export default useDeviceInfoData;
